/**
 * RolePicker: choose platform roles (plan docs/plans/lms-plan.md 8.2
 * "platform roles (administrator, basic_user)", 18.16; WP-43; reused by
 * WP-46).
 *
 * Props (stable):
 *   value      LmsPlatformRole[]                  the chosen roles
 *   onChange   (roles: LmsPlatformRole[]) => void
 *   roles?     LmsPlatformRole[]                  the roles offered (default administrator, basic_user: 8.2)
 *   siteName?  string | null                      named in the hint: a role resolves at the ACTING site (18.16)
 *   label?     string                             the fieldset legend (default "Platform roles")
 *   disabled?  boolean
 *
 * Each role is a toggle chip (aria-pressed) with data-testid="role-option-<role>".
 */
import { chipCls } from '../../../ui/styles';
import type { LmsPlatformRole } from '../../api/client';
import { metaCls, microLabelCls, roleLabel } from './shared';

export interface RolePickerProps {
  value: LmsPlatformRole[];
  onChange: (roles: LmsPlatformRole[]) => void;
  roles?: LmsPlatformRole[];
  siteName?: string | null;
  label?: string;
  disabled?: boolean;
}

/** 8.2: the role selector offers administrator and basic_user. */
export const DEFAULT_PICKER_ROLES: LmsPlatformRole[] = ['administrator', 'basic_user'];

export function RolePicker({
  value,
  onChange,
  roles = DEFAULT_PICKER_ROLES,
  siteName = null,
  label = 'Platform roles',
  disabled = false,
}: RolePickerProps) {
  const toggle = (role: LmsPlatformRole) => {
    onChange(value.includes(role) ? value.filter((r) => r !== role) : [...value, role]);
  };

  return (
    <fieldset data-testid="role-picker" disabled={disabled}>
      <legend className={`${microLabelCls} mb-2`}>{label}</legend>
      <div className="flex flex-wrap gap-1.5">
        {roles.map((role) => {
          const on = value.includes(role);
          return (
            <button
              key={role}
              type="button"
              aria-pressed={on}
              data-testid={`role-option-${role}`}
              onClick={() => toggle(role)}
              className={chipCls(on)}
            >
              {roleLabel(role)}
            </button>
          );
        })}
      </div>
      <p className={`${metaCls} mt-2`}>
        {siteName ? `Everyone with the role at ${siteName}, the site you are working in.` : "Everyone with the role at the site you are working in."}
      </p>
    </fieldset>
  );
}

export default RolePicker;
